
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { SearchResult } from './types';
import { calculateRelevance, detectQueryType } from './utils';
import { mapDbStoreProductToStoreProduct } from '@/shared/mappers/storeProductMappers';
import type { DbStoreProduct } from '@/types/entities/store-product';

export const searchStoreProducts = async (query: string, limit: number, userId?: string): Promise<SearchResult[]> => {
  const queryType = detectQueryType(query);

  type Thenable<T> = { then: (onfulfilled: (value: T) => void, onrejected: (reason: unknown) => void) => unknown };
  const wrapThenable = <T>(qb: Thenable<T>): Promise<T> => new Promise((resolve, reject) => qb.then(resolve, reject));

  const baseSelect = (uid?: string) => {
    let qb = supabaseWorker
      .from('store_products')
      .select('id, name, price, stock, created_at, user_id, updated_at, rental_point_id, category_id');
    if (uid) {
      qb = qb.eq('user_id', uid);
    }
    return qb;
  };

  type DbRow = Pick<DbStoreProduct, 'id' | 'name' | 'price' | 'created_at' | 'user_id' | 'updated_at' | 'rental_point_id' | 'category_id'> & {
    stock: number | null;
  };
  // Используем unknown для совместимости с FetchQueryBuilder (then возвращает unknown)
  let promises: Array<Promise<{ data: unknown | null; error: unknown | null }>> = [];

  if (queryType === 'id') {
    promises = [
      wrapThenable(baseSelect(userId).eq('id', query).limit(limit)),
      wrapThenable(baseSelect(userId).ilike('id', `${query}%`).limit(limit)),
      wrapThenable(baseSelect(userId).ilike('name', `%${query}%`).limit(limit)),
    ];
  } else {
    promises = [
      wrapThenable(baseSelect(userId).ilike('name', `%${query}%`).limit(limit)),
    ];
  }

  const settled = await Promise.allSettled(promises);
  let hadRejection = false;
  const rows = settled.flatMap(s => {
    if (s.status === 'fulfilled') {
      const r = s.value as { data: unknown | null };
      const data = r?.data as unknown;
      return Array.isArray(data) ? (data as DbRow[]) : [];
    } else {
      hadRejection = true;
      // eslint-disable-next-line no-console
      console.error('[GlobalSearch][store] sub-query rejected:', s.reason);
      return [];
    }
  });
  if (rows.length === 0 && hadRejection) {
    throw new Error('Store search source failed');
  }
  const unique = Array.from(new Map(rows.map(r => [r.id, r])).values()).slice(0, limit);

  const products = unique.map(dbProduct => mapDbStoreProductToStoreProduct(dbProduct as unknown as DbStoreProduct));

  return products.map(product => ({
    id: product.id,
    type: 'store' as const,
    title: product.name || 'Без названия',
    subtitle: 'Товар',
    description: `${product.price || 0} ₽ (в наличии: ${product.stock || 0})`,
    icon: 'Store',
    url: `/store`,
    relevance: calculateRelevance(query, [product.name], queryType),
    createdAt: product.createdAt.toISOString(),
    status: product.stock && product.stock > 0 ? 'available' : 'out_of_stock'
  }));
};
